import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal, readGrandTotalOrZero } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { buildColumnChartOptions } from './chartHelpers';

export default function ReadmissionRateHeader({
  loading,
  rollups,
  highestEntity,
  entityRows,
  entityLabel,
}: MetricHeaderProps) {
  const readmissionRate = readGrandTotal(rollups, Metrics.READMISSION_RATE);
  const readmissionsSum = readGrandTotalOrZero(rollups, Metrics.READMISSIONS_SUM);
  const totalDischarges = readGrandTotalOrZero(rollups, Metrics.ID_COUNT);
  const avgLos = readGrandTotal(rollups, Metrics.READMISSION_LENGTH_OF_STAY_AVERAGE);

  const { options: chartOptions, scrollConfig } = useMemo(
    () =>
      buildColumnChartOptions({
        entityRows,
        seriesName: METRIC_CONFIGS[NetworkPerformanceMetrics.READMISSION_RATE].label,
        color: colors.red100,
        yAxisFormat: '{value}%',
        tooltipFormatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `${this.key}: ${formatPercentage(this.y ?? null)}`;
        },
      }),
    [entityRows]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={METRIC_CONFIGS[NetworkPerformanceMetrics.READMISSION_RATE].primaryMetricLabel}
            loading={loading}
            primary={formatPercentage(readmissionRate)}
            addendum={`${readmissionsSum} of ${totalDischarges} discharges`}
            tone='red'
          />
          <StatTile
            label='Most Readmissions'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={highestEntity ? `${formatPercentage(highestEntity.value)} readmission rate` : undefined}
          />
          <StatTile
            label='Avg. Length of Service for Readmissions'
            loading={loading}
            primary={formatDaysValue(avgLos)}
            addendum={`Based on ${formatWholeNumber(readmissionsSum)} readmissions`}
          />
        </>
      }
      chartTitle={`${METRIC_CONFIGS[NetworkPerformanceMetrics.READMISSION_RATE].label} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      scrollConfig={scrollConfig}
      emptyMessage='No readmission data to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
